import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import Apply from "@/pages/Apply";
import { api, track } from "@/lib/api";
import { Overline } from "@/components/primitives";

const INVALID_COPY = {
  expired: {
    title: "This invitation has expired.",
    body: "Private application links remain open for 7 days. If you would still like your property to be considered for the Collection, you are welcome to request consideration directly.",
  },
  used: {
    title: "This application has already been submitted.",
    body: "Each private invitation link can be used once. Our Selection Team has received the application connected to this link and will review it personally.",
  },
  invalid: {
    title: "This link is no longer valid.",
    body: "The invitation you are trying to open could not be verified. If you believe this is an error, please contact the VillaFlow team or request consideration directly.",
  },
};

function InvalidView({ reason }) {
  const copy = INVALID_COPY[reason] || INVALID_COPY.invalid;
  return (
    <div className="min-h-[100svh] bg-navy-900 flex flex-col items-center justify-center text-center px-6 py-24" data-testid="share-link-invalid" data-reason={reason}>
      <Logo variant="full" className="h-16" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="max-w-xl">
        <h1 className="mt-12 font-serif font-light text-4xl md:text-5xl text-ivory tracking-tight">{copy.title}</h1>
        <p className="mt-6 text-ivory/60 font-light leading-relaxed">{copy.body}</p>
        <div className="gold-rule my-10 max-w-[100px] mx-auto" />
        <Link to="/apply" className="inline-block border border-gold/50 hover:border-gold text-ivory hover:text-gold px-10 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-500" data-testid="share-invalid-apply-link">
          Request Consideration
        </Link>
      </motion.div>
    </div>
  );
}

function CountdownCell({ value, label, testid }) {
  return (
    <div className="border border-gold/25 bg-navy-800/60 px-4 py-5 md:px-8 md:py-7 min-w-[72px] md:min-w-[104px]">
      <span className="block font-serif font-light text-3xl md:text-5xl text-gold tabular-nums" data-testid={testid}>
        {String(value).padStart(2, "0")}
      </span>
      <span className="mt-2 block text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-ivory/45">{label}</span>
    </div>
  );
}

export default function ShareApply() {
  const { code } = useParams();
  const [st, setSt] = useState({ loading: true, valid: false, reason: "invalid", label: "", expiresAt: null });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let active = true;
    api
      .get(`/share-links/${encodeURIComponent(code || "")}`)
      .then((r) => {
        if (!active) return;
        const d = r.data || {};
        setSt({ loading: false, valid: !!d.valid, reason: d.reason || "invalid", label: d.label || "", expiresAt: d.expires_at || null });
        if (d.valid) track("share_link_opened", { code });
      })
      .catch(() => active && setSt({ loading: false, valid: false, reason: "invalid", label: "", expiresAt: null }));
    return () => { active = false; };
  }, [code]);

  useEffect(() => {
    if (!st.valid) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [st.valid]);

  if (st.loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center" data-testid="share-link-loading">
        <Loader2 className="animate-spin text-gold/60" size={28} />
      </div>
    );
  }

  const remaining = st.expiresAt ? Math.max(0, new Date(st.expiresAt).getTime() - now) : 0;
  if (!st.valid || remaining <= 0) {
    return <InvalidView reason={st.valid ? "expired" : st.reason} />;
  }

  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  return (
    <div className="bg-navy-900 min-h-screen" data-testid="share-apply-page">
      <header className="pt-10 flex justify-center">
        <Link to="/" aria-label="VillaFlow"><Logo variant="full" className="h-14" /></Link>
      </header>

      <section className="px-6 pt-14 pb-4 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="max-w-2xl mx-auto">
          <Overline className="justify-center">Personal Invitation{st.label ? ` · ${st.label}` : ""}</Overline>
          <h1 className="mt-6 font-serif font-light text-4xl md:text-6xl text-ivory tracking-tight leading-[1.05]">
            You've been personally invited.
          </h1>
          <p className="mt-6 text-ivory/60 font-light leading-relaxed">
            VillaFlow Private Collection is intentionally limited. This private link reserves your
            application window — our Selection Team is expecting your property.
          </p>

          <div className="mt-10 flex items-stretch justify-center gap-2 md:gap-4" data-testid="share-countdown">
            <CountdownCell value={days} label="Days" testid="share-count-days" />
            <CountdownCell value={hours} label="Hours" testid="share-count-hours" />
            <CountdownCell value={minutes} label="Minutes" testid="share-count-minutes" />
            <CountdownCell value={seconds} label="Seconds" testid="share-count-seconds" />
          </div>
          <p className="mt-6 font-serif italic text-gold/80 text-lg">Complete your application before the invitation expires.</p>
        </motion.div>
      </section>

      <Apply embedded shareLinkCode={code} />
    </div>
  );
}
